import { Head, router } from "@inertiajs/react"
import { useCallback, useState } from "react"

import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerSettingsNotificationsPath } from "@/routes"

interface NotificationItem {
  key: string
  label: string
  description: string
  enabled: boolean
}

type NotificationGroups = Record<string, NotificationItem[]>

function Toggle({
  enabled,
  onToggle,
}: {
  enabled: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
        enabled ? "bg-zinc-950 dark:bg-white" : "bg-zinc-200 dark:bg-zinc-700"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform dark:bg-zinc-950 ${
          enabled ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  )
}

const GROUP_LABELS: Record<string, string> = {
  accounts: "Accounts",
  billing: "Billing",
  agents: "Agents",
  team: "Team",
}

export default function NotificationSettings({
  notification_groups,
}: {
  notification_groups: NotificationGroups
}) {
  const [settings, setSettings] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    for (const items of Object.values(notification_groups)) {
      for (const item of items) {
        initial[item.key] = item.enabled
      }
    }
    return initial
  })

  const handleToggle = useCallback(
    (key: string) => {
      const newValue = !settings[key]
      setSettings((prev) => ({ ...prev, [key]: newValue }))

      router.patch(
        partnerSettingsNotificationsPath(),
        { notifications: { [key]: newValue } },
        { preserveState: true, preserveScroll: true },
      )
    },
    [settings],
  )

  return (
    <PartnerLayout>
      <Head title="Settings — Notifications" />
      <PartnerSettingsLayout>
        <div className="flex max-w-lg flex-col gap-8">
          {Object.entries(notification_groups).map(([group, items]) => (
            <div key={group} className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold">
                {GROUP_LABELS[group] ?? group}
              </h2>
              <div>
                {items.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between border-b py-4 last:border-0"
                  >
                    <div>
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-muted-foreground text-sm">
                        {item.description}
                      </div>
                    </div>
                    <Toggle
                      enabled={settings[item.key] ?? item.enabled}
                      onToggle={() => handleToggle(item.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
